import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { notifApi } from '../../api/notifications.js';

const TABS = [
  { key: 'all',       label: 'All' },
  { key: 'unread',    label: 'Unread' },
  { key: 'issue',     label: 'Issues' },
  { key: 'approval',  label: 'Approvals' },
  { key: 'mention',   label: 'Mentions' },
  { key: 'leave',     label: 'Leave' },
  { key: 'wiki',      label: 'Wiki' },
  { key: 'system',    label: 'System' },
];

export default function NotificationCenter() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [tab, setTab] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    const params = { limit: 200 };
    if (tab === 'unread') params.unread = 'true';
    else if (tab !== 'all') params.category = tab;
    const r = await notifApi.list(params);
    setItems(r?.items || []);
    setUnread(r?.unread || 0);
    setSelected(new Set());
  }, [tab]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    return items.filter((n) => {
      if (dateFrom && new Date(n.created_at) < new Date(dateFrom)) return false;
      if (dateTo && new Date(n.created_at) > new Date(dateTo + 'T23:59:59')) return false;
      return true;
    });
  }, [items, dateFrom, dateTo]);

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleExpand(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAllVisible() {
    setSelected(new Set(filtered.map((n) => n.id)));
  }
  function clearSelection() { setSelected(new Set()); }

  async function bulkRead() {
    if (!selected.size) return;
    await notifApi.bulkRead([...selected]);
    reload();
  }
  async function bulkDelete() {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} notification(s)?`)) return;
    await notifApi.removeBulk([...selected]);
    reload();
  }
  async function clearReadOnly() {
    if (!window.confirm('Delete all read notifications?')) return;
    await notifApi.clearRead();
    reload();
  }
  async function markAll() {
    await notifApi.markAllRead();
    reload();
  }

  return (
    <section className="page" data-testid="notification-center" style={{ padding: '20px 24px', maxWidth: 980, margin: '0 auto' }}>
      <header className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Notifications</h1>
        <span className="muted small">{unread} unread</span>
        <div style={{ flex: 1 }} />
        <Link to="/profile/notifications/settings" className="btn ghost small">Preferences</Link>
        <button className="btn ghost small" onClick={markAll} disabled={!unread} data-testid="center-mark-all">Mark all read</button>
        <button className="btn ghost small" onClick={clearReadOnly} data-testid="center-clear-read">Clear read</button>
      </header>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`center-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`btn ghost small${tab === t.key ? ' active' : ''}`}
            style={{
              borderColor: tab === t.key ? '#0369a1' : undefined,
              background: tab === t.key ? '#e0f2fe' : undefined,
              color: tab === t.key ? '#0369a1' : undefined,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#475569' }}>
          From
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#475569' }}>
          To
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <div style={{ flex: 1 }} />
        {selected.size > 0 && (
          <>
            <span className="muted small">{selected.size} selected</span>
            <button className="btn ghost small" onClick={bulkRead} data-testid="center-bulk-read">Mark read</button>
            <button className="btn small" onClick={bulkDelete} data-testid="center-bulk-delete" style={{ color: '#dc2626' }}>Delete</button>
            <button className="btn ghost small" onClick={clearSelection}>Clear</button>
          </>
        )}
        {selected.size === 0 && filtered.length > 0 && (
          <button className="btn ghost small" onClick={selectAllVisible} data-testid="center-select-all">Select all</button>
        )}
      </div>

      {filtered.length === 0 && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }} data-testid="center-empty">
          <p className="muted">{tab === 'all' ? "You're all caught up! 🎉" : `No ${TABS.find((t) => t.key === tab).label.toLowerCase()} notifications`}</p>
        </div>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="center-list">
        {filtered.map((n) => {
          const isUnread = !n.is_read && !n.read_at;
          const isExpanded = expanded.has(n.id);
          return (
            <li key={n.id}
                data-testid={`center-row-${n.id}`}
                data-unread={isUnread ? 'true' : 'false'}
                style={{
                  display: 'flex', gap: 12, padding: '12px 14px',
                  background: isUnread ? '#f0f9ff' : '#fff',
                  borderBottom: '1px solid #e2e8f0',
                }}>
              <input
                type="checkbox"
                checked={selected.has(n.id)}
                onChange={() => toggleSelect(n.id)}
                aria-label="Select"
                style={{ marginTop: 4 }}
              />
              <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => toggleExpand(n.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="chip" style={{ fontSize: 10, textTransform: 'uppercase' }}>{n.category || 'system'}</span>
                  <span style={{ fontSize: 13, fontWeight: isUnread ? 600 : 500 }}>{n.title}</span>
                  {isUnread && <span style={{ width: 8, height: 8, borderRadius: 4, background: '#3b82f6', marginLeft: 'auto' }} />}
                </div>
                {n.body && <div className="muted small" style={{ marginTop: 4, whiteSpace: isExpanded ? 'pre-wrap' : 'normal', overflow: isExpanded ? 'visible' : 'hidden', textOverflow: 'ellipsis' }}>{n.body}</div>}
                <div className="muted small" style={{ marginTop: 4, display: 'flex', gap: 8 }}>
                  <span>{relTime(n.created_at)}</span>
                  {n.actor_name && <span>by {n.actor_name}</span>}
                  {n.resource_url && <button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); navigate(n.resource_url); }}>Open →</button>}
                </div>
                {isExpanded && n.metadata && Object.keys(n.metadata || {}).length > 0 && (
                  <pre style={{ marginTop: 6, padding: 6, background: '#f8fafc', fontSize: 11, borderRadius: 4 }}>{JSON.stringify(n.metadata, null, 2)}</pre>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
